// @ts-nocheck
/**
 * GPU/canvas boat layer for the columnar live-map path (flag-gated; mounted only when config.LIVE_MAP_GPU).
 *
 * Replaces the D3-SVG per-boat overlay for live replay. A SINGLE <canvas> over the map canvas container is
 * redrawn on the ~5 Hz boat playhead (and on map render/move for reprojection). Positions/headings come from
 * `liveColumnarFrame.sampleColumnarFrame()` (binary-search + lerp over the columnar ring). ~12 rotated glyphs
 * + labels per redraw — cheap, no d3 transitions, no SVG DOM churn.
 *
 * Isolation: live-map only. Reuses heading-smoothing helpers from trackInterpolation.
 */

import { createEffect, onCleanup, onMount } from 'solid-js';
import { sourcesStore } from '../../../../../store/sourcesStore';
import {
  smoothPlaybackTimeForBoat,
  isPlaying,
  isLiveReplayPlayheadReady,
} from '../../../../../store/playbackStore';
import { liveColumnarStore } from '../../../../../store/liveColumnarStore';
import { persistantStore } from '../../../../../store/persistantStore';
import { sampleColumnarFrame, POSITION_CHANNELS } from '../../../../../utils/liveColumnarFrame';
import { smoothHeadingDeg, normalizeRotationDeg } from '../../../../../utils/trackInterpolation';
import type { LiveReplaySourceStyle } from '../../../../../utils/liveReplayFramePoints';
import { resolveBoatMarkerStyle } from '../../renderers/boatMarkerStyle';
import { drawWindArrowOnCanvas } from '../../renderers/windArrowGeometry';

export interface GlBoatLayerProps {
  map: any;
  mapContainer: HTMLElement;
  selectedSourceIds: Set<number>;
  sourceStyles?: Map<number, LiveReplaySourceStyle>;
  /**
   * Averaged wind per boat name, supplied only while the wind overlay wants boat wind shown. Present → each
   * listed boat gets the same downwind arrow + TWS label the marks carry.
   */
  boatWindByName?: Map<string, { tws: number; twd: number }>;
}

// Boat glyphs (bow at +y); rotate(hdg+180-bearing) points the bow to heading/north — matches the SVG layer.
// Paths ported verbatim from LiveMultiBoatLayer.createBoatIcon.
const MONOHULL_PATH: Array<[number, number]> = [
  [0, -12], [-4, -12], [-4, 0], [-2, 8], [0, 12], [2, 8], [4, 0], [4, -12],
];
const MULTIHULL_PATH: Array<[number, number]> = [
  [0, 12], [5.332, 0], [5.332, 6], [6.665, 12], [8, 6], [8, -12],
  [-8, -12], [-8, 6], [-6.665, 12], [-5.332, 6], [-5.332, 0],
];

/**
 * How long a boat may be carried forward on its last known position before it stops being drawn.
 *
 * The carry-forward exists so brief NaN-GPS gaps don't blink boats off the map, so this has to comfortably
 * exceed a realistic dropout. But it must be bounded: without a cutoff a source that stops producing
 * entirely stays pinned at its final position for the life of the page, which is how a stopped simulation's
 * boats kept sitting where the replay left them.
 */
const CARRY_FORWARD_MAX_MS = 60_000;

export default function GlBoatLayer(props: GlBoatLayerProps) {
  let canvas: HTMLCanvasElement | null = null;
  const lastHdg = new Map<number, number>();
  // Last valid position per source. Holes in the position columns are now bridged inside the sampler
  // (bracketFinite scans out to the nearest rows carrying coordinates), so this only covers a source with no
  // position anywhere in the sampler's window — it must never become the reason a boat lags its own track.
  const lastGood = new Map<number, { lng: number; lat: number; hdg: number | undefined }>();

  const container = (): HTMLElement | null => {
    try {
      return props.map?.getCanvasContainer?.() || props.mapContainer || null;
    } catch {
      return props.mapContainer || null;
    }
  };

  // Size from the map's own canvas (CSS px). The .mapboxgl-canvas-container div has 0 computed height, so its
  // bounding rect gave height 0 → a 1px-tall overlay. getCanvas().clientWidth/Height is the real map size.
  const sizeCanvas = () => {
    const map = props.map;
    if (!canvas || !map?.getCanvas) return false;
    const mc = map.getCanvas();
    const w = mc.clientWidth || mc.width || 0;
    const h = mc.clientHeight || mc.height || 0;
    if (w <= 0 || h <= 0) return false;
    const dpr = window.devicePixelRatio || 1;
    const cw = Math.round(w * dpr);
    const ch = Math.round(h * dpr);
    if (canvas.width !== cw || canvas.height !== ch) {
      canvas.width = cw;
      canvas.height = ch;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }
    return true;
  };

  const nameColor = (id: number): { name: string; color: string } => {
    const styled = props.sourceStyles?.get(id);
    const name = styled?.sourceName || liveColumnarStore.sourceName(id) || `Source ${id}`;
    const color = styled?.color || sourcesStore.getSourceColor(name) || '#1f77b4';
    return { name, color };
  };

  const boatScale = (): number => {
    const map = props.map;
    if (!map?.getZoom) return 1;
    const zoom = map.getZoom();
    const lat = map.getCenter ? map.getCenter().lat : 0;
    const mpp = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
    const targetPx = 22 / mpp; // ~22 m boat
    return Math.min(12, Math.max(0.75, targetPx / 24));
  };

  const draw = () => {
    if (!canvas || !props.map) return;
    sizeCanvas(); // keep in sync with the map canvas (fixes 0-height container at mount / on resize)
    const dpr = window.devicePixelRatio || 1;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);

    const selected = props.selectedSourceIds || new Set<number>();
    if (selected.size === 0) return;
    const playhead = smoothPlaybackTimeForBoat();
    const playheadMs = playhead instanceof Date ? playhead.getTime() : 0;
    if (!isLiveReplayPlayheadReady(playheadMs)) return;

    // Identify boats by NAME, not by the raw selection id. The columnar store/frames are keyed by the server's
    // stream-assigned source id (a discovery index, NOT the frontend selection id), reconciled via the arrow
    // source_registry. Resolve each selected boat's name -> streamed source id and SKIP any source the server
    // is not streaming this session (e.g. a class boat that is not racing). This prevents a phantom/mislabelled
    // boat caused by a numeric collision between the selection ids and the stream-assigned ids.
    const renderList: Array<{ frameId: number; name: string; color: string }> = [];
    const frameIds = new Set<number>();
    for (const id of selected) {
      const { name, color } = nameColor(id);
      const frameId = liveColumnarStore.sourceId(name);
      if (frameId === undefined) continue; // not currently streamed -> do not render
      // Registered but no POSITION for longer than a plausible GPS dropout: stop carrying it forward rather
      // than leaving it pinned at a position the boat is no longer at. The freshness of the ring as a whole
      // is the wrong test — a boat whose GPS has died still receives rig/wind rows, so `latestTsFor` never
      // ages out and the marker stayed pinned indefinitely.
      // Hand the store the same floor we are about to test against, so a boat whose GPS stopped long
      // ago costs a short scan instead of a full-ring one. `draw()` runs on the map's render/move
      // events, so this is per boat per FRAME while panning. The floor cannot change the outcome:
      // anything below it fails the very next line.
      const newest = liveColumnarStore.latestFiniteTsFor(
        frameId,
        POSITION_CHANNELS,
        playheadMs - CARRY_FORWARD_MAX_MS
      );
      if (newest == null || playheadMs - newest > CARRY_FORWARD_MAX_MS) {
        lastGood.delete(frameId);
        lastHdg.delete(frameId);
        continue;
      }
      frameIds.add(frameId);
      renderList.push({ frameId, name, color });
    }
    if (frameIds.size === 0) return;

    const frame = sampleColumnarFrame(frameIds, playheadMs);
    const bearing = props.map.getBearing ? props.map.getBearing() : 0;
    const boatWind = props.boatWindByName;
    const baseScale = boatScale();
    const iconType = (persistantStore.selectedClassIcon?.() || 'monohull').toLowerCase();
    const path = iconType === 'multihull' ? MULTIHULL_PATH : MONOHULL_PATH;
    const scale = iconType === 'multihull' ? baseScale * 0.75 : baseScale; // 0.75 multihull reduction (parity)

    // Iterate resolved boats: carry forward the last valid position (keyed by stream source id) through gaps.
    for (const { frameId, name, color } of renderList) {
      const fresh = frame.get(frameId);
      if (fresh && Number.isFinite(fresh.lat) && Number.isFinite(fresh.lng)) {
        lastGood.set(frameId, fresh);
      }
      const sample = lastGood.get(frameId);
      if (!sample) continue; // no valid position yet for this source
      const pt = props.map.project([sample.lng, sample.lat]);
      if (!pt || Number.isNaN(pt.x) || Number.isNaN(pt.y)) continue;

      let rotDeg = lastHdg.get(frameId) ?? 0;
      if (sample.hdg != null && Number.isFinite(sample.hdg)) {
        const smooth = smoothHeadingDeg(lastHdg.get(frameId), sample.hdg);
        lastHdg.set(frameId, smooth);
        rotDeg = normalizeRotationDeg(smooth + 180 - bearing);
      }

      ctx.save();
      ctx.translate(pt.x, pt.y);
      ctx.rotate((rotDeg * Math.PI) / 180);
      ctx.scale(scale, scale);
      ctx.beginPath();
      ctx.moveTo(path[0][0], path[0][1]);
      for (let i = 1; i < path.length; i++) ctx.lineTo(path[i][0], path[i][1]);
      ctx.closePath();
      const boatMarkerStyle = resolveBoatMarkerStyle(name, color, '#000');
      ctx.fillStyle = boatMarkerStyle.fill;
      ctx.strokeStyle = boatMarkerStyle.stroke;
      ctx.lineWidth = 1;
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      // Label (unrotated, to the right of the boat)
      ctx.save();
      ctx.translate(pt.x + 12 * Math.max(1, scale * 0.5), pt.y);
      ctx.font = '11px sans-serif';
      ctx.fillStyle = '#fff';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.textBaseline = 'middle';
      ctx.strokeText(name, 0, 0);
      ctx.fillText(name, 0, 0);
      ctx.restore();

      // Wind arrow + TWS label, same convention as the marks (points downwind, locked to the map bearing).
      // Drawn in the boat's own color so it reads as this boat's measurement, not a mark's.
      const wind = boatWind?.get(name);
      if (wind && Number.isFinite(wind.twd) && Number.isFinite(wind.tws)) {
        drawWindArrowOnCanvas(
          ctx,
          { x: pt.x, y: pt.y, twd: wind.twd, mapBearing: bearing },
          { color: boatMarkerStyle.fill, label: wind.tws.toFixed(1) }
        );
      }
    }

    // Drop carried-forward state for sources no longer being rendered (deselected or no longer streamed).
    for (const key of [...lastGood.keys()]) if (!frameIds.has(key)) { lastGood.delete(key); lastHdg.delete(key); }
  };

  onMount(() => {
    const el = container();
    if (!el) return;
    canvas = document.createElement('canvas');
    canvas.className = 'gl-boat-overlay';
    Object.assign(canvas.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: '1000',
    });
    el.appendChild(canvas);
    sizeCanvas();

    const onRender = () => draw();
    const onResize = () => {
      sizeCanvas();
      draw();
    };
    props.map?.on?.('render', onRender);
    props.map?.on?.('move', onRender);
    props.map?.on?.('resize', onResize);
    window.addEventListener('resize', onResize);

    onCleanup(() => {
      props.map?.off?.('render', onRender);
      props.map?.off?.('move', onRender);
      props.map?.off?.('resize', onResize);
      window.removeEventListener('resize', onResize);
      if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
      canvas = null;
    });
  });

  // Redraw on the boat playhead (~5 Hz), store epoch, and the 1 Hz boat-wind update.
  createEffect(() => {
    void liveColumnarStore.epoch();
    void smoothPlaybackTimeForBoat();
    void isPlaying();
    void props.boatWindByName;
    draw();
  });

  return null;
}
